import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("apollo_token");
    if (!token) {
      setLoading(false);
      return;
    }
    // Hard cap bootstrap at 6s so the loading screen never hangs — if the network
    // is slow we just drop the user at /login and they can retry.
    let settled = false;
    const failsafe = setTimeout(() => {
      if (!settled) setLoading(false);
    }, 6000);
    try {
      const { data } = await api.get("/auth/me", { timeout: 6000 });
      settled = true;
      setUser(data);
    } catch {
      settled = true;
      localStorage.removeItem("apollo_token");
    } finally {
      clearTimeout(failsafe);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  async function login(location) {
    try {
      const { data } = await api.post("/auth/login", { location });
      localStorage.setItem("apollo_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  }

  function logout() {
    localStorage.removeItem("apollo_token");
    setUser(null);
    window.location.href = "/login";
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
